import { FC, useContext, useEffect, useState } from "react";
import Blockly from "blockly/core";
import {
  BlocklyWorkspace,
  ToolboxDefinition,
  WorkspaceSvg,
} from "react-blockly";
// import Theme from "@blockly/theme-modern";
import { pythonGenerator } from "blockly/python";
import { IBlocklyProps } from "../types";
import workspaceConfiguration from "./workspaceConfiguration.json";
import { Screen } from "../helpers/Screen";
import { FileSystem } from "../helpers/FileSystem";
import cn from "classnames";
import { LanguageContext } from "../../../contexts/languageContext";

import * as Tr from "blockly/msg/tr";
import { RidersDark, RidersLight } from "./BlocklyThemes";

const urlParams = new URLSearchParams(window.location.search);
var ApiUrl = process.env.REACT_APP_CDN_URL;
if (window.location.hostname.includes("editor-staging.riders.ai")) {
  ApiUrl = process.env.REACT_APP_STAGING_CDN_URL;
}

if (
  window.location.hostname.includes("localhost") &&
  urlParams.has("localcdn")
) {
  ApiUrl = "/cdn";
}

export const BEditor: FC<IBlocklyProps> = (props) => {
  const { className, setCode, setXML, project, file_path } = props;
  const { theme } = useContext<any>(Screen.context);
  const [blocklyTheme, setBlocklyTheme] = useState(
    theme === "dark" ? "riders-dark" : "riders-light"
  );

  const [blocklySettingsOverride, setBlocklySettingsOverride] =
    useState<any>(undefined);

  const isZelos = project?.blocklyTheme === "zelos";

  const setDarkThemeTextColor = () => {
    const labels = document.querySelectorAll(".blocklyTreeLabel");
    for (let i = 0; i < labels.length; i++) {
      if (theme === "dark") {
        labels[i].classList.add("blocklyTreeLabelDark");
      } else {
        labels[i].classList.remove("blocklyTreeLabelDark");
      }
    }
  };

  useEffect(() => {
    setBlocklyTheme(theme === "dark" ? "riders-dark" : "riders-light");
  }, [theme]);

  const { readFile } = useContext<any>(FileSystem.context);

  const [userXML, setUserXML] = useState<string | undefined>(undefined);

  const [hover, setHover] = useState(false);

  const { language } = useContext<any>(LanguageContext);

  const blocklyChangeEvent = new CustomEvent("blocklyChange", {
    detail: {
      message: "Blockly değişti",
      time: new Date(),
      // Diğer istediğiniz bilgiler...
    },
  });

  useEffect(() => {
    setUserXML(undefined);
    readFile(file_path).then((code: string) => {
      setUserXML(code);
    });
  }, [file_path, theme]);

  const [toolboxCategories, setToolboxCategories] = useState<
    any | ToolboxDefinition
  >({
    kind: "categoryToolbox",
    contents: [],
  }); // Empty initial toolbox, if it will not find toolboxCategories.json

  const workspaceChange = (workspace: WorkspaceSvg) => {
    window.getBlocklyWorkspace = (CB: any) => {
      CB(workspace);
    };

    var code = pythonGenerator.workspaceToCode(workspace);
    setCode(code);
    try {
      const _panel: SVGAElement | null = document.querySelector(
        "#blockly_wrapper > div > div > svg:nth-child(7)"
      );
      if (_panel && _panel.style.display === "none")
        (document.querySelector(
          "#blockly_wrapper > div > div > svg:nth-child(8)"
        ) as SVGAElement)!.style.transform = "";
    } catch (ignored) {}
    setDarkThemeTextColor();
  };

  const onXmlChange = (newXML: string) => {
    if (userXML !== newXML) {
      setXML(newXML);
      window.dispatchEvent(blocklyChangeEvent);
    }
  };

  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      let data = null;

      if (!project?.toolboxPath) {
        const file = await readFile(".blockly/toolbox.json");
        data = JSON.parse(file);
      } else {
        data = await fetch(
          `${ApiUrl}/${project.toolboxPath.replace(
            "{language}",
            language.toLowerCase()
          )}`
        ).then((res) => res.json());
      }

      setToolboxCategories({
        kind: "categoryToolbox",
        contents: data.contents.map((item: any) => {
          return {
            ...item,
            categoryStyle:
              isZelos && !item.colour
                ? item.name.toLowerCase() + "_category"
                : false,
          };
        }),
      });
    })();
  }, [project]);

  useEffect(() => {
    async function fetchBlockData() {
      try {
        // Make Blockly related modules available in the global scope
        const Blockly = await import("blockly");
        const BlocklyPython = await import("blockly/python");
        window.Blockly = Blockly;
        window.BlocklyPython = BlocklyPython.pythonGenerator;
        // Get BlockGenerator script
        let initializerURL = null;
        let generatorURL = null;
        let settingsJson = null;

        if (project.folderUrl) {
          initializerURL = project.folderUrl + "initblocks.js";
          generatorURL = project.folderUrl + "generateblocks.js";
          settingsJson = project.folderUrl + "blocklySettings.json";

          if (project.toolboxPath.includes("{language}")) {
            const path = project.toolboxPath.split("/").slice(0, -1).join("/");
            initializerURL =
              ApiUrl +
              "/" +
              path.replace("{language}", language.toLowerCase()) +
              "/initblocks.js";
            generatorURL =
              ApiUrl +
              "/" +
              path.replace("{language}", language.toLowerCase()) +
              "/generateblocks.js";
          }
        }

        const scripts = [
          document.createElement("script"),
          document.createElement("script"),
        ];

        if (initializerURL && generatorURL) {
          scripts[0].src = initializerURL;
          scripts[1].src = generatorURL;

          // Only first script's "onload" function defined
          // on second script's "onload" function, we check if both scripts are loaded
          scripts[0].onload = async () => {
            for (let i = 0; i < 40; i++) {
              if (window.blockly_generate_ready && window.blockly_init_ready) {
                setScriptLoaded(true);
                break;
              }
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          };
        } else {
          const initText = await readFile(".blockly/initblocks.js");
          const generateText = await readFile(".blockly/generateblocks.js");
          scripts[0].type = "text/javascript";
          scripts[0].text = initText;
          scripts[1].type = "text/javascript";
          scripts[1].text = generateText;

          setScriptLoaded(true);
        }

        document.body.appendChild(scripts[0]);
        document.body.appendChild(scripts[1]);

        if (settingsJson) {
          try {
            const settings = await fetch(settingsJson).then((res) =>
              res.json()
            );

            setBlocklySettingsOverride(settings);
          } catch (error) {
            console.error("Error loading settings.json:", error);
          }
        }
      } catch (error) {
        console.error(error);
      }
    }
    fetchBlockData();
  }, [project]);

  useEffect(() => {
    if (window.blocklyThemeLoaded) return;

    if (language === "TR") {
      Blockly.setLocale(Tr);
    }

    Blockly.Theme.defineTheme("riders-light", RidersLight(isZelos));
    Blockly.Theme.defineTheme("riders-dark", RidersDark(isZelos));

    window.blocklyThemeLoaded = true;
  }, []);

  if (!scriptLoaded || !project) {
    return (
      <div className={className} id="blockly_wrapper">
        <div className="h-full flex flex-col justify-center items-center">
          <span className="mb-6 text-2xl">Loading Block Coding</span>
          <div style={{ display: "flex" }}>
            <div className="dot dot--one"></div>
            <div className="dot dot--two"></div>
            <div className="dot dot--three"></div>
            <div className="dot dot--four"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(className, {
        "zelos-theme p-2": isZelos,
        hoverBlockly: isZelos,
      })}
      id="blockly_wrapper"
    >
      {userXML && (
        <BlocklyWorkspace
          className="h-full"
          toolboxConfiguration={toolboxCategories}
          workspaceConfiguration={{
            media: workspaceConfiguration.media,
            renderer: isZelos ? "zelos" : "geras",
            zoom: {
              ...workspaceConfiguration.zoom,
              startScale: isZelos ? 0.75 : 0.9,
            },
            theme: blocklyTheme,
            grid:
              theme === "dark"
                ? { spacing: 20, length: 3, colour: "#000", snap: true }
                : isZelos
                ? { spacing: 20, length: 3, colour: "#ccc", snap: true }
                : undefined,
            move: {
              scrollbars: {
                horizontal: true,
                vertical: true,
              },
              drag: true,
              wheel: true,
            },
            ...blocklySettingsOverride,
          }}
          initialXml={userXML}
          onInject={workspaceChange}
          onWorkspaceChange={workspaceChange}
          onXmlChange={onXmlChange}
        />
      )}
    </div>
  );
};
